import { useState, useEffect, useRef } from "react";
import { UploadSimple, MagicWand, Trash, FileText, Plus, WarningCircle, CircleNotch, ArrowLineDown, ArrowsLeftRight, CheckCircle, PencilSimple, MapPin, ShieldCheck, ArrowSquareOut, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const _daNum = (s) => { const m = String(s ?? "").replace(/,/g, "").match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
const _daNorm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
function computeDeedActual(vd) {
  vd = vd || {};
  const out = [];
  [["north", "North"], ["south", "South"], ["east", "East"], ["west", "West"]].forEach(([k, lbl]) => {
    const dd = vd[`boundary_${k}_deed`], aa = vd[`boundary_${k}_actual`];
    if (_daNorm(dd) && _daNorm(aa) && _daNorm(dd) !== _daNorm(aa)) out.push({ text: `Boundary (${lbl}): deed “${dd}” differs from actual “${aa}”.`, severity: "high" });
    const dn = vd[`dim_${k}_deed`], an = vd[`dim_${k}_actual`];
    if (_daNorm(dn) && _daNorm(an) && _daNorm(dn) !== _daNorm(an)) out.push({ text: `Dimension (${lbl}): deed “${dn}” differs from actual “${an}”.`, severity: "low" });
  });
  const de = _daNum(vd.extent_of_site);
  const aa = _daNum(vd.land_area_sqft) || _daNum(vd.total_land_area_sqft);
  if (de && aa) {
    const deCmp = de < aa / 1000 ? de * 43560 : de;
    if (Math.abs(deCmp - aa) / aa > 0.02) out.push({ text: `Extent: deed / stated area ${de} vs area adopted for valuation ${aa} differ materially (check units).`, severity: "high" });
  }
  return out;
}

const PARAM_RULES = [
  { re: /khasra/i, field: "khasra_no" },
  { re: /boundary.*north|north.*boundary|uttar/i, field: "boundary_north_deed" },
  { re: /boundary.*south|south.*boundary|dakshin/i, field: "boundary_south_deed" },
  { re: /boundary.*east|east.*boundary|purab|poorab/i, field: "boundary_east_deed" },
  { re: /boundary.*west|west.*boundary|paschim/i, field: "boundary_west_deed" },
  { re: /purchaser|cretan|kreta|vendee|buyer|owner|applicant/i, field: "owner_name" },
  { re: /land\s*use|usage|diversion|converted|aavasiya/i, field: "land_use_type" },
  { re: /property\s*location|location|situated|postal/i, field: "postal_address" },
  { re: /plot\s*dimension|dimension/i, field: "total_extent" },
  { re: /ward/i, field: "ward_no" },
  { re: /village|gram\b/i, field: "village" },
  { re: /tehsil|tahsil/i, field: "tehsil" },
  { re: /district|zila|jila/i, field: "district" },
  { re: /p\.?\s*h\.?\s*no|patwari\s*halka/i, field: "ph_no" },
  { re: /p\.?\s*c\.?\s*no/i, field: "pc_no" },
  { re: /bandobast/i, field: "bandobast_no" },
  { re: /sale\s*consideration|consideration\s*amount|sale\s*value|transaction\s*value|विक्रय|प्रतिफल/i, field: "sale_consideration" },
  { re: /stamp\s*duty|स्टाम्प/i, field: "stamp_duty" },
  { re: /registration\s*(no|number)|document\s*(no|number)|deed\s*(no|number)|पंजीयन\s*क्रमांक/i, field: "registration_no" },
  { re: /registration\s*date|date\s*of\s*registration|पंजीयन\s*दिनांक/i, field: "registration_date" },
  { re: /sub.?registrar|sro\b|उप.?पंजीयक/i, field: "sro_office" },
  { re: /nature\s*of\s*deed|deed\s*type|type\s*of\s*deed|विलेख\s*प्रकार/i, field: "deed_type" },
];

// Map AI-extracted document parameters (label/value pairs) onto Valuation Details field keys.
function deriveFieldsFromParams(documents) {
  const out = {};
  for (const d of documents || []) {
    for (const p of d.parameters || []) {
      const label = String(p.label || "");
      const value = String(p.value || "").trim();
      if (!label || !value) continue;
      if (/area|rakba/i.test(label)) {
        const sqft = value.replace(/,/g, "").match(/([\d.]+)\s*sq\.?\s*ft/i);
        if (sqft && !out.total_land_area_sqft) { out.total_land_area_sqft = String(Math.round(parseFloat(sqft[1]))); out.land_area_sqft = out.total_land_area_sqft; }
        const hect = value.match(/([\d.]+)\s*hect/i);
        if (hect && !out.total_rakba) out.total_rakba = hect[1];
        if (!sqft && !hect && !out.extent_of_site) out.extent_of_site = value;
        continue;
      }
      const rule = PARAM_RULES.find((r) => r.re.test(label));
      if (rule && !out[rule.field]) out[rule.field] = value;
    }
  }
  return out;
}

// Map AI-extracted parameters onto top-level Basic job fields (applicant / address).
function deriveBasicFromParams(documents) {
  const out = {};
  for (const d of documents || []) {
    for (const p of d.parameters || []) {
      const label = String(p.label || "");
      const value = String(p.value || "").trim();
      if (!value) continue;
      if (/purchaser|cretan|kreta|vendee|buyer|applicant|owner/i.test(label) && !out.applicant_name) out.applicant_name = value;
      if (/property\s*location|location|situated|address/i.test(label) && !out.property_address) { out.property_address = value; out.property_location = value; }
    }
  }
  return out;
}

export const ESSENTIAL_CHECKS = [
  { key: "bhunaksha", label: "Bhunaksha (Cadastral / Khasra Map)", url: "https://bhunaksha.cg.nic.in/" },
  { key: "bhuiya", label: "Bhuiya (Land Records — B-1 / Khasra)", url: "https://bhuiyan.cg.nic.in" },
  { key: "revenue_dispute", label: "Revenue Dispute Check (राजस्व न्यायालय छ.ग.)", url: "https://revenue.cg.nic.in/" },
  { key: "cersai", label: "CERSAI (Existing Charge / Mortgage Registry)", url: "https://www.cersai.org.in/" },
  { key: "ngdrs", label: "NGDRS (Document Registration System)", url: "https://ngdrs.gov.in/" },
];
const CHECK_STATUSES = ["Pending", "Verified", "Not Applicable", "Discrepancy Found"];
const CHECK_STATUS_CLASS = {
  "Verified": "text-green-700 border-green-300 bg-green-50",
  "Not Applicable": "text-slate-600 border-slate-300 bg-slate-50",
  "Pending": "text-amber-700 border-amber-300 bg-amber-50",
  "Discrepancy Found": "text-red-700 border-red-300 bg-red-50",
};

export default function DocumentsTab({ jobId, value, onChange, vd, onApplyFields, onApplyBasic, onApproveRefresh, onCoords, onEnsureJob, onContinue }) {
  const obs = value || {};
  const documents = obs.documents || [];
  const fields = obs.fields || {};
  const derivedFields = deriveFieldsFromParams(documents);
  const mergedFields = { ...derivedFields, ...fields };
  const filledFields = Object.keys(mergedFields).filter((k) => String(mergedFields[k] ?? "").trim());
  const hasFields = filledFields.length > 0;
  const cautions = computeDeedActual(vd || {});
  const ec = (vd && vd.essential_checks) || {};
  const setCheck = (key, upd) => onCoords && onCoords({ essential_checks: { ...ec, [key]: { ...(ec[key] || {}), ...upd } } });
  const checkFileRefs = useRef({});
  const [shotUploading, setShotUploading] = useState({});
  const uploadCheckShot = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = jobId || (onEnsureJob && (await onEnsureJob()));
    if (!id) { if (e.target) e.target.value = ""; return; }
    setShotUploading((m) => ({ ...m, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/${id}/upload-image`, fd);
      setCheck(key, { screenshot: data.path });
      toast.success(/\.pdf$/i.test(data.path) ? "PDF attached" : "Screenshot attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setShotUploading((m) => ({ ...m, [key]: false })); if (e.target) e.target.value = ""; }
  };
  const applyAll = (docs, fieldsObj) => {
    const derived = deriveFieldsFromParams(docs);
    const merged = { ...derived, ...(fieldsObj || {}) };
    const n = Object.keys(merged).filter((k) => String(merged[k] ?? "").trim()).length;
    if (onApplyFields && n) onApplyFields(merged);
    const basic = deriveBasicFromParams(docs);
    if (onApplyBasic && Object.keys(basic).length) onApplyBasic(basic);
    return n + Object.keys(basic).length;
  };
  const applyFields = () => {
    const n = applyAll(documents, fields);
    toast.success(`Auto-filled ${n} field(s) across Basic & Valuation tabs`);
  };
  const doApproveRefresh = () => {
    const approvedObs = { ...obs, documents: (obs.documents || []).map((d) => ({ ...d, approved: true })) };
    onChange(approvedObs);
    onApproveRefresh(approvedObs);
  };
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef();

  const loadDocs = (id) => { const jid = id || jobId; if (jid) api.get(`/jobs/${jid}/documents`).then((r) => setDocs(r.data)).catch(() => {}); };
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [jobId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = jobId || (onEnsureJob && (await onEnsureJob()));
    if (!id) { if (fileRef.current) fileRef.current.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { await api.post(`/jobs/${id}/documents`, fd); toast.success("Document uploaded"); loadDocs(id); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeDoc = async (id) => { try { await api.delete(`/jobs/${jobId}/documents/${id}`); loadDocs(); } catch { toast.error("Delete failed"); } };
  const [labelEdit, setLabelEdit] = useState({});
  const saveLabel = async (id) => {
    const label = (labelEdit[id] || "").trim();
    try {
      await api.patch(`/jobs/${jobId}/documents/${id}`, { label });
      setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, label } : d)));
      setLabelEdit((m) => { const n = { ...m }; delete n[id]; return n; });
      toast.success("Name saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const analyze = async () => {
    const id = jobId || (onEnsureJob && (await onEnsureJob()));
    if (!id) return;
    setAnalyzing(true);
    let obsNew = null;
    try {
      const r = await api.post(`/jobs/${id}/analyze-documents`, {}, { timeout: 240000 });
      obsNew = r.data;
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "The AI reader timed out — the document may be large or the service is busy. Please try again."
        : formatApiError(err.response?.data?.detail);
      toast.error(msg);
      setAnalyzing(false);
      return;
    }
    try {
      onChange(obsNew);
      const n = applyAll(obsNew.documents || [], obsNew.fields || {});
      toast.success(`AI read complete — ${n} field(s) auto-filled across Basic & Valuation. Review & adjust below.`);
    } catch (e) {
      toast.success("AI read complete — review the observations below.");
    } finally {
      setAnalyzing(false);
    }
  };

  const setObs = (upd) => onChange({ ...obs, ...upd });
  const setDoc = (i, upd) => setObs({ documents: documents.map((d, k) => (k === i ? { ...d, ...upd } : d)) });
  const addDoc = () => setObs({ documents: [...documents, { filename: "", doc_type: "", parameters: [], red_flags: [] }] });
  const removeDocEntry = (i) => setObs({ documents: documents.filter((_, k) => k !== i) });
  const setParam = (i, j, upd) => setDoc(i, { parameters: (documents[i].parameters || []).map((p, k) => (k === j ? { ...p, ...upd } : p)) });
  const addParam = (i) => setDoc(i, { parameters: [...(documents[i].parameters || []), { label: "", value: "" }] });
  const removeParam = (i, j) => setDoc(i, { parameters: (documents[i].parameters || []).filter((_, k) => k !== j) });
  const setFlag = (i, idx, v) => setDoc(i, { red_flags: (documents[i].red_flags || []).map((f, k) => (k === idx ? v : f)) });
  const addFlag = (i) => setDoc(i, { red_flags: [...(documents[i].red_flags || []), ""] });
  const removeFlag = (i, idx) => setDoc(i, { red_flags: (documents[i].red_flags || []).filter((_, k) => k !== idx) });

  if (!jobId && !onEnsureJob) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="documents-tab">
        Save the job first, then come back here to upload documents and run the AI reader.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <p className="text-xs text-muted-foreground">Upload the property documents (sale deed, khasra, tax receipts, approvals…). The AI reads them and drafts the <b>Important Observations</b> — document by document — which you can edit before they print in the report.</p>
      {!jobId && <p className="text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5" data-testid="doc-newjob-note">Uploading your first document (or clicking “Create Job &amp; Continue”) creates this job and auto-assigns its VAL ID — then the AI reader and auto-fill become available.</p>}

      {/* Site coordinates up front */}
      <div className="rounded-lg border p-3 bg-background space-y-2" data-testid="doc-site-coords">
        <Label className="text-sm font-semibold flex items-center gap-1.5"><MapPin size={15} /> Site latitude &amp; longitude</Label>
        <p className="text-[11px] text-muted-foreground">Enter the field-checked coordinates up front — they drive the live satellite base in the GIS tab and print in the report.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-[11px]">Latitude</Label><Input value={vd?.field_lat ?? ""} onChange={(e) => onCoords && onCoords({ field_lat: e.target.value })} placeholder="e.g. 21.251098" className="h-8 text-xs" data-testid="doc-site-lat" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Longitude</Label><Input value={vd?.field_lon ?? ""} onChange={(e) => onCoords && onCoords({ field_lon: e.target.value })} placeholder="e.g. 81.629641" className="h-8 text-xs" data-testid="doc-site-lon" /></div>
        </div>
      </div>

      {/* Uploaded documents */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Documents on this job</Label>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="doc-upload-input"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx" />
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn">
              <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
            <Button type="button" size="sm" className="rounded-full text-xs bg-primary hover:bg-[#113A64]" disabled={analyzing || docs.length === 0} onClick={analyze} data-testid="doc-analyze-btn">
              {analyzing ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <MagicWand size={14} className="mr-1" />} {analyzing ? "Reading documents…" : "Analyze with AI"}
            </Button>
            {onApproveRefresh && (obs.documents || []).length > 0 && (
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={doApproveRefresh} data-testid="doc-approve-refresh"><CheckCircle size={14} className="mr-1" /> Approve all &amp; regenerate report</Button>
            )}
          </div>
        </div>
        {docs.length === 0
          ? <p className="text-xs text-muted-foreground">No documents uploaded yet.</p>
          : docs.map((d) => (
            <div key={d.id} className="border rounded-md px-2.5 py-1.5" data-testid={`doc-file-row-${d.id}`}>
              <div className="flex items-center gap-2 text-xs">
                <FileText size={14} className="text-primary shrink-0" />
                <a href={`${api.defaults.baseURL}/jobs/${jobId}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="flex-1 truncate hover:underline">
                  {d.label ? <span><span className="font-semibold text-primary">{d.label}</span> <span className="text-muted-foreground">· {d.filename}</span></span> : d.filename}
                </a>
                <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-accent" title={d.label ? "Edit name" : "Add a name / nomenclature"}
                  onClick={() => setLabelEdit((m) => ({ ...m, [d.id]: m[d.id] != null ? m[d.id] : (d.label || "") }))} data-testid={`doc-name-add-${d.id}`}>
                  {d.label ? <PencilSimple size={12} /> : <Plus size={13} weight="bold" />}
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeDoc(d.id)} data-testid={`doc-del-${d.id}`}><Trash size={12} /></Button>
              </div>
              {labelEdit[d.id] != null && (
                <div className="flex items-center gap-2 mt-1.5 pl-5" data-testid={`doc-name-editor-${d.id}`}>
                  <Input autoFocus value={labelEdit[d.id]} onChange={(e) => setLabelEdit((m) => ({ ...m, [d.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") saveLabel(d.id); }} placeholder="e.g. Sale Deed 2019 / Khasra B-12 / Tax Receipt"
                    className="h-7 text-xs" data-testid={`doc-name-input-${d.id}`} />
                  <Button type="button" size="sm" className="h-7 text-xs rounded-full bg-primary hover:bg-[#113A64]" onClick={() => saveLabel(d.id)} data-testid={`doc-name-save-${d.id}`}>Save</Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLabelEdit((m) => { const n = { ...m }; delete n[d.id]; return n; })}>Cancel</Button>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Deed vs Actual check (optional) */}
      <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid="deed-actual-check">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-semibold flex items-center gap-1.5"><ArrowsLeftRight size={15} /> Deed vs Actual check</Label>
          <Switch checked={!!vd?.deed_actual_check} onCheckedChange={(v) => onCoords && onCoords({ deed_actual_check: v })} data-testid="deed-actual-toggle" />
        </div>
        <p className="text-[11px] text-muted-foreground">Optional. When ON, deed / document boundaries, dimensions and extent are compared against the surveyed (actual) values and any mismatch prints in the report as a caution.</p>
        {!vd?.deed_actual_check
          ? <p className="text-xs text-muted-foreground" data-testid="deed-actual-off">Turned off — no deed-vs-actual cautions will appear in the report.</p>
          : cautions.length === 0
            ? <p className="text-xs text-green-700" data-testid="deed-actual-ok">No discrepancies detected between deed and actual values.</p>
            : <ul className="space-y-1">{cautions.map((c, i) => (<li key={i} className={"text-xs flex items-start gap-1.5 " + (c.severity === "high" ? "text-red-700" : "text-amber-700")} data-testid={`deed-actual-item-${i}`}><WarningCircle size={13} className="mt-0.5 shrink-0" /><span className="flex-1">{c.text}</span><span className={"text-[9px] uppercase font-semibold px-1 rounded " + (c.severity === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>{c.severity === "high" ? "High" : "Minor"}</span></li>))}</ul>}
      </div>

      {/* Essential statutory / legal verification checks */}
      <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid="essential-checks">
        <Label className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck size={15} /> Essential Checks — Statutory / Legal Verification</Label>
        <p className="text-[11px] text-muted-foreground">Record the mandatory government-portal verifications. Status &amp; remarks print as a dedicated annexure in the report.</p>
        <div className="space-y-2">
          {ESSENTIAL_CHECKS.map((c) => {
            const rec = ec[c.key] || {};
            const status = rec.status || "Pending";
            return (
              <div key={c.key} className="rounded-md border p-2.5 space-y-2 bg-muted/20" data-testid={`check-${c.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold">{c.label}</div>
                    <a href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline" data-testid={`check-link-${c.key}`}>
                      <ArrowSquareOut size={12} /> Open portal
                    </a>
                  </div>
                  <select
                    className={`h-7 shrink-0 rounded-md border px-2 text-[11px] font-medium ${CHECK_STATUS_CLASS[status]}`}
                    value={status}
                    onChange={(e) => setCheck(c.key, { status: e.target.value })}
                    data-testid={`check-status-${c.key}`}
                  >
                    {CHECK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={rec.ref_no || ""} onChange={(e) => setCheck(c.key, { ref_no: e.target.value })} placeholder="Reference no." className="h-7 text-[11px]" data-testid={`check-ref-${c.key}`} />
                  <DatePicker value={rec.date || ""} onChange={(v) => setCheck(c.key, { date: v })} placeholder="Date" clearable className="w-full" data-testid={`check-date-${c.key}`} />
                </div>
                <Input value={rec.remarks || ""} onChange={(e) => setCheck(c.key, { remarks: e.target.value })} placeholder="Remarks / findings" className="h-7 text-[11px]" data-testid={`check-remarks-${c.key}`} />
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf" className="hidden" ref={(el) => (checkFileRefs.current[c.key] = el)} onChange={(e) => uploadCheckShot(c.key, e)} data-testid={`check-shot-input-${c.key}`} />
                  {rec.screenshot ? (
                    <>
                      {/\.pdf($|\?)/i.test(rec.screenshot) ? (
                        <a href={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(rec.screenshot)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-10 px-2 rounded border bg-muted/40 text-[11px] text-primary hover:underline" data-testid={`check-shot-thumb-${c.key}`}>
                          <FileText size={14} /> View PDF
                        </a>
                      ) : (
                        <a href={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(rec.screenshot)}`} target="_blank" rel="noreferrer">
                          <img src={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(rec.screenshot)}`} alt="proof" className="h-10 w-16 object-cover rounded border" data-testid={`check-shot-thumb-${c.key}`} />
                        </a>
                      )}
                      <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-destructive" onClick={() => setCheck(c.key, { screenshot: "" })} data-testid={`check-shot-remove-${c.key}`}><Trash size={12} className="mr-1" /> Remove</Button>
                    </>
                  ) : (
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] rounded-full" disabled={shotUploading[c.key]} onClick={() => checkFileRefs.current[c.key]?.click()} data-testid={`check-shot-btn-${c.key}`}>
                      {shotUploading[c.key] ? <CircleNotch size={12} className="mr-1 animate-spin" /> : <UploadSimple size={12} className="mr-1" />} {shotUploading[c.key] ? "Uploading…" : "Attach image / PDF"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transaction details from deed */}
      <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid="transaction-details">
        <Label className="text-sm font-semibold flex items-center gap-1.5"><Receipt size={15} /> Transaction details (from registered deed)</Label>
        <p className="text-[11px] text-muted-foreground">Sale consideration, stamp duty and registration details read by the AI auto-fill here and print in a dedicated report section so nothing extracted is overlooked. Edit if needed.</p>
        <div className="grid grid-cols-2 gap-2">
          {[["deed_type", "Nature of deed"], ["registration_no", "Registration / Doc no."], ["registration_date", "Date of registration"], ["sro_office", "Sub-Registrar (SRO)"], ["sale_consideration", "Sale consideration (₹)"], ["stamp_duty", "Stamp duty (₹)"]].map(([k, lbl]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[11px]">{lbl}</Label>
              <Input value={(vd && vd[k]) || ""} onChange={(e) => onCoords && onCoords({ [k]: e.target.value })} className="h-7 text-[11px]" data-testid={`txn-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* AI observations editor */}
      <div className="rounded-lg border p-3 space-y-3 bg-background" data-testid="doc-observations">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-semibold">Important observations from documents provided</Label>
          <div className="flex items-center gap-2">
            {hasFields && onApplyFields && (
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={applyFields} data-testid="doc-autofill-btn"><ArrowLineDown size={13} className="mr-1" /> Auto-fill Basic &amp; Valuation</Button>
            )}
            {obs.model && <span className="text-[10px] text-muted-foreground">AI: {obs.model}</span>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Overall summary</Label>
          <Textarea value={obs.summary || ""} onChange={(e) => setObs({ summary: e.target.value })} rows={3} placeholder="Run the AI reader, or write the key observations here." className="text-xs" data-testid="doc-summary" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">Per-document findings</Label>
            <div className="flex items-center gap-2">
              {documents.length > 0 && (
                <label className="flex items-center gap-1.5 text-[11px] font-medium cursor-pointer" data-testid="doc-approve-all-label">
                  <input type="checkbox" checked={documents.length > 0 && documents.every((d) => d.approved)} onChange={(e) => setObs({ documents: documents.map((d) => ({ ...d, approved: e.target.checked })) })} data-testid="doc-approve-all" />
                  Approve all
                </label>
              )}
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={addDoc} data-testid="doc-add-document"><Plus size={12} className="mr-1" /> Add document</Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Only documents marked <b>“Reviewed &amp; approved”</b> print in the report.</p>
          {documents.length === 0 && <p className="text-[11px] text-muted-foreground">No findings yet — click “Analyze with AI”.</p>}
          {documents.map((d, i) => (
            <div key={i} className="rounded-lg border p-2.5 space-y-2 bg-muted/20" data-testid={`doc-doc-${i}`}>
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1 flex-1 min-w-[140px]"><Label className="text-[11px]">File</Label><Input value={d.filename || ""} onChange={(e) => setDoc(i, { filename: e.target.value })} className="h-8 text-xs" data-testid={`doc-filename-${i}`} /></div>
                <div className="space-y-1 flex-1 min-w-[140px]"><Label className="text-[11px]">Document type</Label><Input value={d.doc_type || ""} onChange={(e) => setDoc(i, { doc_type: e.target.value })} placeholder="e.g. Registered Sale Deed" className="h-8 text-xs" data-testid={`doc-type-${i}`} /></div>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeDocEntry(i)} data-testid={`doc-del-entry-${i}`}><Trash size={14} /></Button>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] font-medium cursor-pointer w-fit">
                <input type="checkbox" checked={!!d.approved} onChange={(e) => setDoc(i, { approved: e.target.checked })} data-testid={`doc-approve-${i}`} />
                Reviewed &amp; approved {d.approved ? <span className="text-green-700">· will print</span> : <span className="text-muted-foreground">· hidden from report</span>}
              </label>
              <div className="space-y-1">
                {(d.parameters || []).map((p, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Input value={p.label} onChange={(e) => setParam(i, j, { label: e.target.value })} placeholder="Parameter" className="h-7 text-xs flex-1" data-testid={`doc-param-label-${i}-${j}`} />
                    <Input value={p.value} onChange={(e) => setParam(i, j, { value: e.target.value })} placeholder="As per document" className="h-7 text-xs flex-1" data-testid={`doc-param-value-${i}-${j}`} />
                    <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeParam(i, j)}><Trash size={12} /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6 px-2" onClick={() => addParam(i)} data-testid={`doc-add-param-${i}`}><Plus size={11} className="mr-1" /> Add parameter</Button>
              </div>
              {(d.red_flags || []).length > 0 && (
                <div className="space-y-1 border-t pt-1.5">
                  {(d.red_flags || []).map((f, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <WarningCircle size={13} className="text-amber-600 shrink-0" />
                      <Input value={f} onChange={(e) => setFlag(i, idx, e.target.value)} placeholder="Point for attention" className="h-7 text-xs flex-1" data-testid={`doc-flag-${i}-${idx}`} />
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeFlag(i, idx)}><Trash size={12} /></Button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6 px-2 text-amber-700" onClick={() => addFlag(i)} data-testid={`doc-add-flag-${i}`}><Plus size={11} className="mr-1" /> Add point for attention</Button>
            </div>
          ))}
        </div>
      </div>
      {onContinue && (
        <div className="flex items-center justify-between gap-2 border-t pt-3" data-testid="doc-continue-bar">
          <span className="text-[11px] text-muted-foreground">{jobId ? "Job created — continue filling the valuation." : "Create this job to proceed to the next tab."}</span>
          <Button type="button" className="rounded-full bg-primary hover:bg-[#113A64]" disabled={uploading || analyzing} onClick={async () => { const id = jobId || (onEnsureJob && (await onEnsureJob())); if (id && onContinue) onContinue(); }} data-testid="doc-continue-btn">
            {jobId ? "Continue to Basic Details →" : "Create Job & Continue →"}
          </Button>
        </div>
      )}
    </div>
  );
}
